"use client";

import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { LogOut, Settings } from "lucide-react";
import { cn } from "@/lib/utils";
import { DEFAULT_AVATARS } from "@/lib/default-profile-avatars";
import { getAuthAvatarUrl } from "@/lib/auth-user-metadata";

export interface HomeUserAccountMenuProps {
  /** Used for initials fallback and menu subtitle. */
  email?: string;
  /** Supabase `user.user_metadata` — drives custom / default / OAuth avatar. */
  userMetadata?: Record<string, unknown> | null;
  onLogout: () => void | Promise<void>;
}

function initialsFromEmail(email: string | undefined): string {
  if (!email?.trim()) return "?";
  const local = email.split("@")[0] ?? "";
  const parts = local.split(/[._-]+/).filter(Boolean);
  if (parts.length >= 2 && parts[0][0] && parts[1][0]) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  if (local.length >= 2) return local.slice(0, 2).toUpperCase();
  return (local[0] ?? "?").toUpperCase();
}

function NavAccountAvatar({
  email,
  metadata,
}: {
  email?: string;
  metadata?: Record<string, unknown> | null;
}) {
  const [failedCustom, setFailedCustom] = useState(false);
  const [failedOauth, setFailedOauth] = useState(false);
  const md = metadata ?? {};

  const str = (k: string) =>
    typeof md[k] === "string" ? (md[k] as string).trim() : "";

  const avatarType = str("avatar_type");
  const customUrl = str("custom_avatar_url");
  const useCustom = avatarType === "custom" && customUrl.length > 0;

  const emoji = str("avatar_emoji");
  const colorFromMeta = str("avatar_color");
  const avatarIdRaw = str("avatar");
  const avatarId =
    avatarIdRaw && avatarIdRaw !== "custom" ? avatarIdRaw : "panda";
  const fromList = DEFAULT_AVATARS.find((a) => a.id === avatarId);

  const oauthUrl = getAuthAvatarUrl(md);

  if (useCustom && !failedCustom) {
    return (
      <Image
        src={customUrl}
        alt=""
        width={36}
        height={36}
        className="h-full w-full object-cover"
        onError={() => setFailedCustom(true)}
        unoptimized
      />
    );
  }

  const hasSavedEmoji = Boolean(emoji);

  if (hasSavedEmoji) {
    const gradientColor =
      colorFromMeta || fromList?.color || "from-violet-200 to-orange-200";
    return (
      <span
        className={cn(
          "flex h-full w-full items-center justify-center bg-gradient-to-br text-[1.05rem] leading-none",
          gradientColor,
        )}
        aria-hidden
      >
        {emoji}
      </span>
    );
  }

  if (oauthUrl && !failedOauth) {
    return (
      <Image
        src={oauthUrl}
        alt=""
        width={36}
        height={36}
        className="h-full w-full object-cover"
        onError={() => setFailedOauth(true)}
        unoptimized
      />
    );
  }

  const listEmoji = fromList?.emoji;
  const listColor = fromList?.color || "from-violet-200 to-orange-200";

  if (listEmoji) {
    return (
      <span
        className={cn(
          "flex h-full w-full items-center justify-center bg-gradient-to-br text-[1.05rem] leading-none",
          listColor,
        )}
        aria-hidden
      >
        {listEmoji}
      </span>
    );
  }

  return (
    <span
      className="flex h-full w-full select-none items-center justify-center bg-gradient-to-br from-violet-600 via-fuchsia-600 to-orange-500 text-[11px] font-bold tracking-tight text-white"
      aria-hidden
    >
      {initialsFromEmail(email)}
    </span>
  );
}

/**
 * Compact account control: circular avatar opens a menu with Profile and Log out.
 */
export function HomeUserAccountMenu({
  email,
  userMetadata,
  onLogout,
}: HomeUserAccountMenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="relative shrink-0" ref={rootRef}>
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Account menu"
        title="Account"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "cursor-pointer relative flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full shadow-md ring-2 ring-white/90 transition hover:brightness-[1.03] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 focus-visible:ring-offset-2 dark:ring-zinc-700/80",
          open && "ring-orange-400 dark:ring-orange-400/80",
        )}
      >
        <NavAccountAvatar email={email} metadata={userMetadata} />
      </button>

      {open ? (
        <div
          role="menu"
          aria-orientation="vertical"
          className="absolute right-0 top-[calc(100%+0.5rem)] z-[100] w-[min(calc(100vw-2rem),15rem)] overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-xl ring-1 ring-black/5 dark:ring-white/10"
        >
          <div className="border-b border-border px-3 py-2.5">
            <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              Signed in
            </p>
            <p className="mt-0.5 truncate text-sm font-semibold text-foreground" title={email}>
              {email ?? "Account"}
            </p>
          </div>
          <div className="p-1">
            <Link
              href="/settings"
              role="menuitem"
              className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm font-medium text-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
              onClick={() => setOpen(false)}
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                <Settings className="h-4 w-4" strokeWidth={2} />
              </span>
              <span className="flex-1">Settings</span>
            </Link>
            <button
              type="button"
              role="menuitem"
              className="mt-0.5 flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm font-medium text-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
              onClick={() => {
                setOpen(false);
                void onLogout();
              }}
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                <LogOut className="h-4 w-4" strokeWidth={2} />
              </span>
              Log out
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
